import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useTranslation } from "react-i18next";
import { formatXOF, formatDate, METHOD_LABELS, METHOD_COLORS } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
  GraduationCap, ChalkboardTeacher, CurrencyDollar, ChartLineUp,
  Users, Warning, Sparkle, ArrowRight, Wallet,
} from "@phosphor-icons/react";

const STAT_STYLES = {
  primary: "bg-primary text-primary-foreground",
  accent: "bg-accent text-accent-foreground",
  secondary: "bg-secondary text-secondary-foreground",
  neutral: "bg-muted text-foreground",
};

function Stat({ icon: Icon, label, value, sub, tone = "neutral", testid }) {
  return (
    <div className="bg-card border border-border rounded-lg p-5 card-hover" data-testid={testid}>
      <div className={`w-10 h-10 rounded-md flex items-center justify-center mb-3 ${STAT_STYLES[tone]}`}>
        <Icon size={20} weight="duotone" />
      </div>
      <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">{label}</div>
      <div className="mt-1 font-heading text-2xl font-bold tabular-nums">{value}</div>
      {sub ? <div className="text-xs text-muted-foreground mt-1">{sub}</div> : null}
    </div>
  );
}

export default function Dashboard() {
  const { t } = useTranslation();
  const { user, school, refresh } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/dashboard");
      setStats(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const seedDemo = async () => {
    setSeeding(true);
    try {
      await api.post("/setup/demo");
      toast.success(t("dashboard.seed_success"));
      await refresh();
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSeeding(false); }
  };

  const needsSetup = user?.role !== "super_admin" && (!user?.school_id || stats?.empty);
  const teachers = stats?.teachers_count ?? 0;
  const outstandingCount = stats?.outstanding_count ?? 0;
  const absent = stats?.attendance_absent ?? 0;
  const scope = stats?.scope;

  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto">
      <header className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="text-xs uppercase tracking-widest text-secondary font-semibold">{t("dashboard.kicker")}</div>
          <h1 className="mt-2 font-heading text-3xl md:text-4xl font-bold tracking-tight">
            {t("dashboard.greeting", { name: user?.name?.split(" ")[0] || "" })}
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            {scope?.mode === "global"
              ? <span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-accent animate-pulse" /> Vue globale · <b>{scope.total_schools}</b> établissements agrégés · Année {school?.academic_year || "—"}</span>
              : scope?.mode === "scoped"
              ? <span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-primary" /> {scope.school_name} · {scope.country} · {scope.currency}</span>
              : `${school?.name || t("dashboard.no_school")} · ${t("dashboard.year", { year: school?.academic_year || "—" })}`}
          </p>
        </div>
        <div className="flex gap-2">
          <Link to="/app/eleves"><Button variant="outline" className="rounded-full" data-testid="btn-goto-students">{t("dashboard.btn_students")}</Button></Link>
          <Link to="/app/finances">
            <Button className="bg-accent hover:bg-accent/90 text-accent-foreground rounded-full" data-testid="btn-goto-finances">
              {t("dashboard.btn_collect")} <ArrowRight size={14} className="ml-2" />
            </Button>
          </Link>
        </div>
      </header>

      {needsSetup && (
        <Card className="p-6 mb-8 border-accent bg-accent/5" data-testid="card-setup">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-start gap-4">
              <div className="w-11 h-11 rounded-md bg-accent text-accent-foreground flex items-center justify-center">
                <Sparkle size={20} weight="fill" />
              </div>
              <div>
                <div className="font-heading font-semibold text-lg">{t("dashboard.setup_title")}</div>
                <div className="text-sm text-muted-foreground mt-1 max-w-xl">{t("dashboard.setup_desc")}</div>
              </div>
            </div>
            <Button onClick={seedDemo} disabled={seeding} className="rounded-full" data-testid="btn-seed-demo">
              {seeding ? t("dashboard.setup_loading") : t("dashboard.setup_cta")}
            </Button>
          </div>
        </Card>
      )}

      {loading ? (
        <div className="text-muted-foreground text-sm">{t("dashboard.loading_metrics")}</div>
      ) : stats && !stats.empty ? (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Stat icon={GraduationCap} label={t("dashboard.stat_students")} value={stats.students_count} tone="primary" testid="stat-students" />
            <Stat
              icon={ChalkboardTeacher}
              label={t("dashboard.stat_classes")}
              value={stats.classes_count}
              sub={t(teachers > 1 ? "dashboard.stat_classes_sub_other" : "dashboard.stat_classes_sub_one", { count: teachers })}
              tone="secondary" testid="stat-classes"
            />
            <Stat
              icon={CurrencyDollar}
              label={t("dashboard.stat_revenue_today")}
              value={formatXOF(stats.revenue_today)}
              sub={t("dashboard.stat_revenue_month_sub", { amount: formatXOF(stats.revenue_month) })}
              tone="accent" testid="stat-revenue"
            />
            <Stat
              icon={Wallet}
              label={t("dashboard.stat_treasury")}
              value={formatXOF(stats.treasury)}
              sub={t("dashboard.stat_treasury_sub")}
              tone="neutral" testid="stat-treasury"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
            <Stat
              icon={Warning}
              label={t("dashboard.stat_outstanding")}
              value={formatXOF(stats.outstanding)}
              sub={t(outstandingCount > 1 ? "dashboard.stat_outstanding_sub_other" : "dashboard.stat_outstanding_sub_one", { count: outstandingCount })}
              tone="neutral" testid="stat-outstanding"
            />
            <Stat
              icon={Users}
              label={t("dashboard.stat_attendance")}
              value={stats.attendance_present}
              sub={t("dashboard.stat_attendance_sub", { count: absent })}
              tone="secondary" testid="stat-attendance"
            />
            <Stat
              icon={ChartLineUp}
              label={t("dashboard.stat_month")}
              value={formatXOF(stats.revenue_month)}
              sub={t("dashboard.stat_month_sub")}
              tone="primary" testid="stat-month"
            />
          </div>

          <section className="mt-10">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-xl font-semibold">{t("dashboard.recent_payments")}</h2>
              <Link to="/app/finances" className="text-xs text-accent hover:underline" data-testid="link-see-payments">{t("dashboard.see_all")}</Link>
            </div>
            <div className="bg-card border border-border rounded-lg overflow-hidden">
              {stats.recent_payments?.length === 0 ? (
                <div className="p-8 text-center text-sm text-muted-foreground">
                  {t("dashboard.no_payments_a")} <Link to="/app/finances" className="text-accent">{t("dashboard.no_payments_b")}</Link>
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                      <th className="px-4 py-3 font-semibold">{t("dashboard.col_receipt")}</th>
                      <th className="px-4 py-3 font-semibold">{t("dashboard.col_date")}</th>
                      <th className="px-4 py-3 font-semibold">{t("dashboard.col_method")}</th>
                      <th className="px-4 py-3 font-semibold text-right">{t("dashboard.col_amount")}</th>
                      <th className="px-4 py-3 font-semibold">{t("dashboard.col_cashier")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.recent_payments.map((p, i) => (
                      <tr key={p.id} className={`border-b border-border last:border-0 ${i % 2 ? "bg-muted/30" : ""}`}>
                        <td className="px-4 py-3 font-mono text-xs">{p.receipt_number}</td>
                        <td className="px-4 py-3">{formatDate(p.date)}</td>
                        <td className="px-4 py-3">
                          <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold uppercase tracking-wider ${METHOD_COLORS[p.method]}`}>
                            {METHOD_LABELS[p.method]}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right font-semibold tabular-nums">{formatXOF(p.amount)}</td>
                        <td className="px-4 py-3 text-muted-foreground text-xs">{p.cashier_name}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </section>
        </>
      ) : null}
    </div>
  );
}
